import { useMutation } from '@apollo/client';
import { PencilIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PhoneInputWithCountrySelect from 'react-phone-number-input';
import { Field } from '~/components/shared/Field';
import { formatPhoneNumber } from '~/lib/utils';
import { UPSERT_WHATSAPP_NUMBER } from '~/services/onboarding';
import { StepComponentProps } from '../types';

export const WhatsappTestNumber = ({
  business,
  moveNextStep,
  submitForm,
  setSubmitForm,
  setDisable,
}: StepComponentProps) => {
  const { register, setValue, watch, getValues } = useForm({
    defaultValues: {
      first_name: business.whatsapp?.profile_name,
      whatsapp_number:
        business.whatsapp?.channel_id && `+${business.whatsapp?.channel_id}`,
    },
  });
  const [edit, setEdit] = useState(business.whatsapp ? false : true);
  const { t } = useTranslation('onboarding');
  const [upsertWhatsappNumber] = useMutation(UPSERT_WHATSAPP_NUMBER, {
    onCompleted: (data) => {
      if (data.response.hasError) {
        console.error(data.response.message);
        setSubmitForm(false);
      } else {
        moveNextStep(data.response.data);
      }
    },
    onError: (error) => {
      console.error(error);
      setSubmitForm(false);
    },
  });

  useEffect(() => {
    if (submitForm) {
      if (edit) {
        const data = getValues();
        const payload: any = {
          ...data,
          whatsapp_number: data.whatsapp_number?.replace('+', ''),
          business_id: business._id,
        };

        if (business.test_whatsapp_channel_id)
          payload.channel_id = business.test_whatsapp_channel_id;

        upsertWhatsappNumber({
          variables: {
            input: payload,
          },
        });
      } else {
        moveNextStep();
      }
    }
  }, [submitForm]);

  useEffect(() => {
    if (business.business_name && business.agent_name && business.agent_type) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, []);

  const watchedFields = watch(['first_name', 'whatsapp_number']);

  useEffect(() => {
    const allFilled = watchedFields.every(
      (field) => field && field.trim() !== '',
    );
    setDisable(!allFilled);
  }, [watchedFields]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-3xl font-bold">{t('test_number.title')}</h1>
      <p className="my-4 text-sm">{t('test_number.whatsapp_description')}</p>
      {edit ? (
        <>
          <Field
            register={register}
            label={t('test_number.first_name')}
            fieldName="first_name"
            placeholder={t('test_number.first_name_placeholder')}
          />
          <div className="flex w-full flex-col">
            <span className="label-text py-2 font-bold">
              {t('test_number.whatsapp_field')}
            </span>
            <PhoneInputWithCountrySelect
              value={watch('whatsapp_number')}
              defaultCountry="CO"
              className={`input input-bordered w-full transition-all`}
              onChange={(value) => {
                setValue('whatsapp_number', value as string);
              }}
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between gap-8 rounded-lg border border-custom-surface-400 bg-base-100 p-10">
            <div className="flex flex-col gap-6">
              <p className="text-sm">
                {t('test_number.whatsapp_succesful', {
                  first_name: business.whatsapp?.profile_name,
                })}
              </p>
              <p className="text-md font-bold">
                {formatPhoneNumber(business.whatsapp?.channel_id)}
              </p>
            </div>
            <CheckBadgeIcon className="h-6 w-6 shrink-0 fill-success" />
          </div>
          <div className="flex justify-end">
            <button className="btn" onClick={() => setEdit(true)}>
              <PencilSquareIcon className="h-4" />
              {t('test_number.edit')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
